import React, { useState, useRef, useEffect } from 'react';
import { IconSearch } from '../../../icons';

interface MultiSelectAutocompleteProps {
  titulo: string;
  placeholder: string;
  value: string[];
  onChange: (values: string[]) => void;
  options: string[];
  searchPlaceholder?: string;
}

const MultiSelectAutocomplete: React.FC<MultiSelectAutocompleteProps> = ({
  titulo,
  placeholder,
  value,
  onChange,
  options,
  searchPlaceholder = "Buscar..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filtered = options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: string) => {
    if (value.includes(option)) {
      // Remove se já estiver selecionado
      onChange(value.filter(item => item !== option));
    } else {
      // Adiciona se não estiver selecionado
      onChange([...value, option]);
    }
  };

  const handleRemoveTag = (optionToRemove: string) => {
    onChange(value.filter(item => item !== optionToRemove));
  };

  const handleInputClick = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const truncateText = (text: string, maxLength: number = 20) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--c2d3748)',
        marginBottom: '0.5rem',
        display: 'block'
      }}>
        {titulo}
      </label>
      
      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Campo principal com tags */}
        <div
          onClick={handleInputClick}
          style={{
            width: '100%',
            minHeight: '48px',
            padding: '8px 12px',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            alignItems: 'center',
            backgroundColor: 'var(--surface-card)'
          }}
        >
          {/* Tags dos itens selecionados */}
          {value.length > 0 ? (
            value.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'var(--cf7fafc)',
                  border: '1px solid var(--ce2e8f0)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '0.75rem',
                  color: 'var(--c4a5568)',
                  gap: '4px'
                }}
              >
                <span>{truncateText(item)}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(item);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--c718096)',
                    cursor: 'pointer',
                    padding: '0',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '16px',
                    height: '16px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#e53e3e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#718096';
                  }}
                >
                  ×
                </button>
              </div>
            ))
          ) : (
            <span style={{ color: 'var(--ca0aec0)' }}>{placeholder}</span>
          )}
          
          {/* Seta do dropdown */}
          <div style={{
            marginLeft: 'auto',
            color: 'var(--c718096)'
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 8L2 4h8L6 8z" />
            </svg>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'hidden'
          }}>
            {/* Campo de busca */}
            <div style={{
              padding: '12px',
              borderBottom: '1px solid var(--ce2e8f0)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--c718096)',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconSearch color="#718096" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  height: '36px',
                  padding: '0 0 0 40px',
                  border: '1px solid var(--ce2e8f0)',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  outline: 'none',
                  backgroundColor: 'var(--surface-input)',
                  color: 'var(--c2d3748)'
                }}
                autoFocus
              />
            </div>

            {/* Lista de opções */}
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isSelected = value.includes(option);
                  return (
                    <div
                      key={index}
                      onClick={() => handleSelect(option)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: 'var(--c2d3748)',
                        borderBottom: index < filteredOptions.length - 1 ? '1px solid var(--cf7fafc)' : 'none',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--ce2e8f0)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Checkbox no estilo do sistema */}
                      <div style={{
                        width: '22px',
                        height: '22px',
                        border: `2px solid ${isSelected ? 'var(--ce52940)' : 'var(--c798891)'}`,
                        borderRadius: '3px',
                        backgroundColor: isSelected ? 'var(--ce52940)' : 'var(--surface-input)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}>
                        {isSelected && (
                          <span style={{
                            color: 'var(--ink-white)',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            opacity: 1,
                            transition: 'opacity 0.2s'
                          }}>
                            ✔
                          </span>
                        )}
                      </div>
                      <span style={{ flex: 1 }}>{option}</span>
                    </div>
                  );
                })
              ) : (
                <div style={{
                  padding: '12px 16px',
                  fontSize: '0.875rem',
                  color: 'var(--c718096)',
                  textAlign: 'center'
                }}>
                  Nenhum resultado encontrado
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectAutocomplete;
